import React, { useState, useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import DownloadModal from './components/DownloadModal';
import InstallPrompt from './components/InstallPrompt';
import Home from './pages/Home';
import Pricing from './pages/Pricing';
import Blog from './pages/Blog';
import FeaturePage from './pages/FeaturePage';
import POSApp from './pages/POSApp';
import PaymentResult from './pages/PaymentResult';
import SellerAuth from './pages/SellerAuth';
import Activate from './pages/Activate';
import ResetPassword from './pages/ResetPassword';
import DeleteAccount from './pages/DeleteAccount';
import { images } from './mock';
import { ContentProvider } from './context/ContentContext';
import { ThemeProvider, ThemeApplier } from './theme/ThemeContext';
import { Toaster } from 'sonner';
import { AdminProvider, ProtectedRoute } from './admin/AdminAuth';
import AdminLogin from './admin/AdminLogin';
import AdminDashboard from './admin/AdminDashboard';

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const PublicLayout = ({ children, onDownload }) => (
  <>
    <Navbar onDownload={onDownload} />
    {children}
    <Footer onDownload={onDownload} />
    <InstallPrompt />
  </>
);

function App() {
  const [modalOpen, setModalOpen] = useState(false);
  const openModal = () => setModalOpen(true);

  return (
    <div className="App">
      <ThemeProvider>
      <AdminProvider>
        <ContentProvider>
          <BrowserRouter>
            <ScrollToTop />
            <ThemeApplier />
            <Routes>
              {/* Admin routes (no public chrome) */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />

              {/* Seller POS app (full-screen, no public chrome) */}
              <Route path="/app" element={<POSApp />} />
              <Route path="/seller" element={<SellerAuth />} />
              <Route path="/activate" element={<Activate />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/delete-account" element={<DeleteAccount />} />
              <Route path="/payment/success" element={<PaymentResult type="success" />} />
              <Route path="/payment/cancel" element={<PaymentResult type="cancel" />} />

              {/* Public routes */}
              <Route path="/" element={<PublicLayout onDownload={openModal}><Home onDownload={openModal} /></PublicLayout>} />
              <Route path="/pricing" element={<PublicLayout onDownload={openModal}><Pricing onDownload={openModal} /></PublicLayout>} />
              <Route path="/blog" element={<PublicLayout onDownload={openModal}><Blog /></PublicLayout>} />
              <Route path="/tap-to-pay" element={<PublicLayout onDownload={openModal}><FeaturePage onDownload={openModal} eyebrow="Tap to Pay" title="Your phone is the" titleAccent="card reader." description="Accept contactless cards and wallets using just your phone. No dongles, no rentals, no hidden costs — and only 1.4% per tap." bullets={['No card reader needed', 'Debit, credit & digital wallets', 'Paid in seconds', 'Just 1.4% per tap']} image={images.contactless} /></PublicLayout>} />
              <Route path="/payment-links" element={<PublicLayout onDownload={openModal}><FeaturePage onDownload={openModal} eyebrow="Payment Links" title="Sell from" titleAccent="anywhere." description="Create and share payment links in one tap. Your customer clicks and pays — no app, no sign-up — straight to your FLIX balance." bullets={['Send by text, DM or email', 'No app for customers', 'Instant payouts', 'Flat 3.9% + $0.30 per link']} image={images.vendor} /></PublicLayout>} />
              <Route path="/storefront" element={<PublicLayout onDownload={openModal}><FeaturePage onDownload={openModal} eyebrow="Storefront" title="Your site, live in" titleAccent="minutes." description="FLIX builds a clean, professional storefront from your business info. No code, no designer, no monthly plan — payments built right in." bullets={['Totally free', 'Add a catalog', 'Payments built in', 'Fully customizable']} image={images.florist2} /></PublicLayout>} />
              <Route path="/flix-ai" element={<PublicLayout onDownload={openModal}><FeaturePage onDownload={openModal} eyebrow="FLIX AI" title="A team in your" titleAccent="pocket." description="A marketing assistant, business analyst and sales specialist in one. FLIX spots patterns, finds opportunities and flags problems before they cost you money." bullets={['Live sales insights', 'Finds local events & demand', 'Writes your marketing', 'Maps your next growth move']} image={images.freelancer} /></PublicLayout>} />
            </Routes>
            <DownloadModal open={modalOpen} onClose={() => setModalOpen(false)} />
          </BrowserRouter>
        </ContentProvider>
      </AdminProvider>
      <Toaster position="top-center" richColors />
      </ThemeProvider>
    </div>
  );
}

export default App;
